import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import {
  TaskChemicalAssessment,
  fetchChemicalProducts,
  fetchCompanyChemicalAssessments,
  createCompanyChemicalAssessment,
  deleteTaskChemicalAssessment,
} from '../lib/gestion-tecnica-constructor';
import {
  ChemicalProduct,
  CoshhCantidad,
  CoshhVolatilidad,
  calcularPeligrosidad,
  calcularVolatilidadProducto,
} from '../lib/coshhEssentials';

const CANTIDADES: { value: CoshhCantidad; label: string }[] = [
  { value: 'pequena', label: 'Pequeña (gramos o mililitros)' },
  { value: 'mediana', label: 'Mediana (kilogramos o litros)' },
  { value: 'grande', label: 'Grande (toneladas o metros cúbicos)' },
];

const PULVERULENCIAS: { value: CoshhVolatilidad; label: string }[] = [
  { value: 'baja', label: 'Baja — no se aprecia polvo al manipular' },
  { value: 'media', label: 'Media — se produce polvo visible, se deposita rápido' },
  { value: 'alta', label: 'Alta — nubes de polvo que permanecen suspendidas' },
];

const NIVEL_COLOR: Record<number, string> = {
  1: '#16a34a',
  2: '#ca8a04',
  3: '#ea580c',
  4: '#dc2626',
};

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

export default function CoshhMethodologyPage() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const companyId = (impersonated ?? company)?.id ?? '';

  const [products, setProducts] = useState<ChemicalProduct[]>([]);
  const [assessments, setAssessments] = useState<TaskChemicalAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [productId, setProductId] = useState('');
  const [cantidad, setCantidad] = useState<CoshhCantidad | ''>('');
  const [temperaturaProceso, setTemperaturaProceso] = useState('');
  const [pulverulenciaManual, setPulverulenciaManual] = useState<CoshhVolatilidad | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const [prods, assess] = await Promise.all([
      fetchChemicalProducts(companyId),
      fetchCompanyChemicalAssessments(companyId),
    ]);
    setProducts(prods.filter(p => p.coshh_h_phrases.length > 0));
    setAssessments(assess);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const selectedProduct = products.find(p => p.id === productId) ?? null;
  const peligrosidad = selectedProduct ? calcularPeligrosidad(selectedProduct.coshh_h_phrases) : null;
  const necesitaTemperatura = selectedProduct?.coshh_estado_fisico === 'liquido';
  const necesitaPulverulencia = selectedProduct?.coshh_estado_fisico === 'solido';

  const volatilidad = selectedProduct
    ? calcularVolatilidadProducto(
        selectedProduct,
        temperaturaProceso.trim() === '' ? 20 : Number(temperaturaProceso),
        pulverulenciaManual || null
      )
    : null;

  async function handleCreate() {
    if (!selectedProduct) { setError('Selecciona un producto.'); return; }
    if (!peligrosidad) { setError('Este producto no tiene frases H clasificadas. Complétalas en Productos primero.'); return; }
    if (!cantidad) { setError('Selecciona la cantidad usada.'); return; }
    if (!volatilidad) { setError('Falta completar la temperatura de proceso o la pulverulencia según el estado físico del producto.'); return; }

    setError('');
    setSaving(true);
    try {
      await createCompanyChemicalAssessment({
        companyId,
        product: selectedProduct,
        peligrosidad,
        cantidad,
        volatilidad,
        pulverulenciaManual: pulverulenciaManual || null,
        createdBy: profile?.id ?? null,
      });
      setProductId('');
      setCantidad('');
      setTemperaturaProceso('');
      setPulverulenciaManual('');
      await loadAll();
    } catch (err: any) {
      setError('Error al guardar: ' + (err.message ?? 'error desconocido'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('¿Eliminar esta evaluación?')) return;
    await deleteTaskChemicalAssessment(id);
    await loadAll();
  }

  if (loading) return <div style={{ padding: 24 }}><p>Cargando...</p></div>;

  return (
    <div style={{ padding: 24, maxWidth: 780 }}>
      <h1 style={{ color: '#e2e8f0' }}>Metodología COSHH</h1>
      <p style={{ color: '#94a3b8', maxWidth: 620 }}>
        Evaluación cualitativa del riesgo por inhalación de agentes químicos
        (COSHH Essentials, NTP 936). Úsala como herramienta de apoyo antes de
        realizar una medición higiénica real: clasifica la peligrosidad de la
        sustancia, la cantidad usada y su tendencia a pasar al ambiente, y
        obtén un nivel de riesgo orientativo con su acción recomendada.
      </p>

      {assessments.length === 0 && <p style={{ color: '#94a3b8' }}>Sin evaluaciones todavía.</p>}

      {assessments.map(a => (
        <div key={a.id} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <strong style={{ color: '#e2e8f0' }}>{a.product_name_snapshot}</strong>
              <p style={{ color: '#94a3b8', fontSize: 12, margin: '4px 0 0' }}>
                Peligrosidad {a.peligrosidad} · Volatilidad {a.volatilidad} · Cantidad {a.cantidad}
              </p>
              <p style={{ color: '#cbd5e1', fontSize: 12, margin: '4px 0 0' }}>{a.accion_recomendada}</p>
            </div>
            <span style={{
              display: 'inline-block', padding: '2px 10px', borderRadius: 6, fontWeight: 700,
              color: 'white', background: NIVEL_COLOR[a.nivel_riesgo],
            }}>
              Nivel {a.nivel_riesgo}
            </span>
          </div>
          <button onClick={() => handleDelete(a.id)} style={{ marginTop: 8, background: 'transparent', border: '1px solid #334155', color: '#fca5a5', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
            Eliminar
          </button>
        </div>
      ))}

      <div style={{ marginTop: 20, background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
        <h4 style={{ marginTop: 0, color: '#e2e8f0' }}>Nueva evaluación</h4>

        {products.length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: 13 }}>
            No hay productos con frases H clasificadas. Completa la clasificación COSHH en la ficha del producto (sección "Higiene Industrial") primero.
          </p>
        )}

        <label style={{ color: '#e2e8f0', display: 'block', marginBottom: 4 }}>Producto</label>
        <SelectBuscable inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Producto" value={productId} onChange={setProductId}
          vacio="— Seleccionar —" opciones={products.map(p => ({ value: p.id, label: p.name }))} />

        {selectedProduct && (
          <p style={{ marginTop: 6, fontSize: 12, color: '#60a5fa' }}>
            Peligrosidad: <strong>{peligrosidad ?? '—'}</strong> · Estado físico: {selectedProduct.coshh_estado_fisico ?? '—'}
          </p>
        )}

        <label style={{ display: 'block', marginTop: 10, marginBottom: 4, color: '#e2e8f0' }}>Cantidad usada</label>
        <select style={inputStyle} value={cantidad} onChange={e => setCantidad(e.target.value as CoshhCantidad)}>
          <option value="" style={{ color: '#000000' }}>— Seleccionar —</option>
          {CANTIDADES.map(c => <option key={c.value} value={c.value} style={{ color: '#000000' }}>{c.label}</option>)}
        </select>

        {necesitaTemperatura && (
          <div style={{ marginTop: 10 }}>
            <label style={{ color: '#e2e8f0', display: 'block', marginBottom: 4 }}>Temperatura del proceso (°C)</label>
            <input
              type="number"
              value={temperaturaProceso}
              onChange={e => setTemperaturaProceso(e.target.value)}
              style={inputStyle}
              placeholder="ej. 20 (temperatura ambiente si no aplica calor)"
            />
          </div>
        )}

        {necesitaPulverulencia && (
          <div style={{ marginTop: 10 }}>
            <label style={{ color: '#e2e8f0', display: 'block', marginBottom: 4 }}>Pulverulencia (tendencia a formar polvo)</label>
            <select style={inputStyle} value={pulverulenciaManual} onChange={e => setPulverulenciaManual(e.target.value as CoshhVolatilidad)}>
              <option value="" style={{ color: '#000000' }}>— Seleccionar —</option>
              {PULVERULENCIAS.map(p => <option key={p.value} value={p.value} style={{ color: '#000000' }}>{p.label}</option>)}
            </select>
          </div>
        )}

        {selectedProduct && volatilidad && (
          <p style={{ marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
            Volatilidad calculada: <strong>{volatilidad}</strong>
          </p>
        )}

        {error && <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{error}</p>}

        <button
          disabled={saving}
          onClick={handleCreate}
          style={{ marginTop: 12, background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}
        >
          {saving ? 'Guardando...' : 'Guardar evaluación'}
        </button>
      </div>
    </div>
  );
}
